import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, TrendingUp, PieChart, BarChart3, ArrowLeft, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ToolSelector from '../../components/ToolSelector';
import PageTutorial from '../../components/PageTutorial';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const AnalyticsDashboard = () => {
 const { API } = useContext(AuthContext);
 const navigate = useNavigate();
 const [insights, setInsights] = useState(null);
 const [segments, setSegments] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchAnalytics();
 }, []);

 const fetchAnalytics = async () => {
 try {
 const [insightsRes, segmentsRes] = await Promise.all([
 axios.get(`${API}/admin/analytics/survey-insights`),
 axios.get(`${API}/admin/analytics/user-segments`)
 ]);
 
 setInsights(insightsRes.data);
 setSegments(segmentsRes.data);
 setLoading(false);
 } catch (error) {
 console.error('Analytics error:', error);
 toast.error('Failed to load analytics');
 setLoading(false);
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
 <p className="text-slate-600 dark:text-neutral-400">Loading analytics...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-background dark:from-slate-900 dark:to-slate-800">
 <ToolSelector />
 <div className="max-w-7xl mx-auto p-6">
 {/* Header */}
 <div className="mb-8">
 <Button
 variant="outline"
 onClick={() => navigate('/admin')}
 className="mb-4"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Admin Portal
 </Button>
 <div className="flex flex-wrap items-start justify-between gap-3">
 <div>
 <span className="eyebrow">Admin · Analytics</span>
 <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 text-foreground">
 Platform Analytics
 </h1>
 <p className="text-muted-foreground text-sm">
 User insights and aggregate data from survey responses.
 </p>
 </div>
 <Button
 variant="outline"
 onClick={async () => {
 try {
 const token = localStorage.getItem('token');
 const res = await fetch(`${API}/admin/equipment/export?format=csv`, {
 headers: token ? { Authorization: `Bearer ${token}` } : {},
 });
 if (!res.ok) throw new Error('Export failed');
 const blob = await res.blob();
 const url = window.URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `fda-equipment-export-${new Date().toISOString().slice(0,10)}.csv`;
 document.body.appendChild(a);
 a.click();
 a.remove();
 window.URL.revokeObjectURL(url);
 toast.success('Equipment export downloaded');
 } catch (e) {
 toast.error('Could not download export');
 }
 }}
 className="rounded-full font-semibold"
 data-testid="export-equipment-btn"
 >
 <Download className="w-4 h-4 mr-2" /> Export equipment CSV
 </Button>
 </div>
 </div>

 <PageTutorial id="admin_analytics" />

 {/* Overview Cards */}
 <div className="grid md:grid-cols-4 gap-6 mb-8">
 <Card>
 <CardHeader>
 <CardTitle className="text-sm font-medium text-slate-600 dark:text-neutral-400">
 Total Users
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-3xl font-bold text-blue-600">
 {insights?.overview?.total_users || 0}
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-sm font-medium text-slate-600 dark:text-neutral-400">
 Surveys Completed
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-3xl font-bold text-green-600">
 {insights?.overview?.total_surveys_completed || 0}
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-sm font-medium text-slate-600 dark:text-neutral-400">
 Completion Rate
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-3xl font-bold text-primary">
 {insights?.overview?.completion_rate || 0}%
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-sm font-medium text-slate-600 dark:text-neutral-400">
 Machines Tracked
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-3xl font-bold text-orange-600">
 {insights?.machines?.total_machines_tracked || 0}
 </div>
 </CardContent>
 </Card>
 </div>

 <div className="grid md:grid-cols-2 gap-6 mb-8">
 {/* User Type Distribution */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Users className="w-5 h-5" />
 User Type Distribution
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {insights?.user_types && Object.entries(insights.user_types).map(([type, count]) => (
 <div key={type} className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className={`w-3 h-3 rounded-full ${type === 'business' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
 <span className="font-medium capitalize">{type}</span>
 </div>
 <span className="text-2xl font-bold">{count}</span>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Machine Ownership */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <TrendingUp className="w-5 h-5" />
 Machine Ownership
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-3 h-3 rounded-full bg-green-500"></div>
 <span className="font-medium">Own Machines</span>
 </div>
 <span className="text-2xl font-bold">{segments?.machine_ownership?.own_machines || 0}</span>
 </div>
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
 <span className="font-medium">Planning Purchase</span>
 </div>
 <span className="text-2xl font-bold">{segments?.machine_ownership?.planning_purchase || 0}</span>
 </div>
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-3 h-3 rounded-full bg-slate-400"></div>
 <span className="font-medium">No Machines</span>
 </div>
 <span className="text-2xl font-bold">{segments?.machine_ownership?.no_machines || 0}</span>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 <div className="grid md:grid-cols-2 gap-6 mb-8">
 {/* Machine Brand Distribution */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <PieChart className="w-5 h-5" />
 Machine Brands
 </CardTitle>
 <CardDescription>Distribution of freeze dryer brands</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-3">
 {insights?.machines?.distribution && Object.entries(insights.machines.distribution)
 .sort((a, b) => b[1] - a[1])
 .map(([brand, count], index) => (
 <div key={brand} className="flex items-center gap-3">
 <div className="flex-1">
 <div className="flex items-center justify-between mb-1">
 <span className="text-sm font-medium">{brand}</span>
 <span className="text-sm text-slate-600 dark:text-neutral-400">{count}</span>
 </div>
 <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
 <div 
 className={`h-2 rounded-full ${
 index === 0 ? 'bg-blue-600' :
 index === 1 ? 'bg-green-600' :
 index === 2 ? 'bg-primary' :
 'bg-orange-600'
 }`}
 style={{ 
 width: `${(count / insights.machines.total_machines_tracked) * 100}%` 
 }}
 ></div>
 </div>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Experience Level Distribution */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <BarChart3 className="w-5 h-5" />
 Experience Levels
 </CardTitle>
 <CardDescription>User experience distribution</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {insights?.experience_levels && Object.entries(insights.experience_levels).map(([level, count]) => (
 <div key={level} className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className={`w-3 h-3 rounded-full ${
 level === 'beginner' ? 'bg-yellow-500' :
 level === 'intermediate' ? 'bg-blue-500' :
 'bg-primary'
 }`}></div>
 <span className="font-medium capitalize">{level}</span>
 </div>
 <span className="text-2xl font-bold">{count}</span>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Top Interests */}
 <Card>
 <CardHeader>
 <CardTitle>Top Interests</CardTitle>
 <CardDescription>Most popular freeze drying interests</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-3 gap-4">
 {insights?.interests && Object.entries(insights.interests)
 .sort((a, b) => b[1] - a[1])
 .slice(0, 9)
 .map(([interest, count]) => (
 <div key={interest} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
 <div className="text-2xl mb-1">
 {interest === 'fruits' ? '🍎' :
 interest === 'vegetables' ? '🥕' :
 interest === 'meats' ? '🥩' :
 interest === 'meals' ? '🍲' :
 interest === 'desserts' ? '🍰' :
 interest === 'dairy' ? '🧀' :
 interest === 'herbs' ? '🌿' :
 interest === 'pet-food' ? '🐕' :
 interest === 'candy' ? '🍬' : '📦'}
 </div>
 <div className="font-semibold capitalize">{interest.replace('-', ' ')}</div>
 <div className="text-sm text-slate-600 dark:text-neutral-400">{count} users</div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>
 </div>
 );
};

export default AnalyticsDashboard;
